/**
 * Layer-2 arrangement intro (SC-4123) — Megaloid-style sentence for humans.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md § Border crossing content layers
 */

export type BorderCrossingSummaryFacts = {
  organizationName?: string | null;
  /** Border crossing point label snapshot (Shipping Options). */
  borderCrossingPoint?: string | null;
  portOfEntry?: string | null;
  /** Document-formatted approximate crossing date. */
  crossingDateDisplay?: string | null;
  /** Approximate crossing time of day (order snapshot). */
  crossingTimeDisplay?: string | null;
};

/** @deprecated Use BorderCrossingSummaryFacts */
export type BorderCrossingMetadataFacts = BorderCrossingSummaryFacts;

export const BORDER_CROSSING_DETAILS_SHIPMENT_FIELD_LABELS = [
  "Organization",
  "Border crossing point",
  "Port of entry",
  "Approximate crossing date",
  "Approximate crossing time",
] as const;

/** Modal (i) copy; keep in sync with composeBorderCrossingSummaryText. */
export const borderCrossingDetailsComposerHelpTooltip = [
  "Prefilled from the order's shipment details when you open this modal.",
  "Starts with: Border crossing will be arranged by {org}.",
  "Adds crossing point, port of entry, and estimated date/time when saved on the order.",
  "PAPS/PARS procedure text prefills Border crossing procedure only.",
  `Fields: ${BORDER_CROSSING_DETAILS_SHIPMENT_FIELD_LABELS.join(", ")}.`,
].join("\n\n");

/**
 * Single arrangement sentence; empty when org name is missing.
 * Wording matches Megaloid carrier intro pattern (without broker/HOC — layer 3 / future template).
 */
export const composeBorderCrossingSummaryText = (facts: BorderCrossingSummaryFacts): string => {
  const org = facts.organizationName?.trim();
  if (!org) {
    return "";
  }

  let text = `Border crossing will be arranged by ${org}`;

  const crossingPoint = facts.borderCrossingPoint?.trim();
  if (crossingPoint) {
    text += ` at the ${crossingPoint} border`;
  }

  const port = facts.portOfEntry?.trim();
  if (port) {
    text += ` at the ${port} port`;
  }

  const date = facts.crossingDateDisplay?.trim();
  if (date) {
    text += ` at estimated date ${date}`;
  }

  const time = facts.crossingTimeDisplay?.trim();
  if (time) {
    text += ` at estimated time ${time}`;
  }

  return `${text}.`;
};

/** @deprecated Use composeBorderCrossingSummaryText */
export const composeBorderCrossingMetadataText = composeBorderCrossingSummaryText;
